import React from 'react';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { SOCIAL_LINKS, TikTokIcon } from "@/components/FloatingDock";

export function Footer() {
  const settings = useSiteSettings();
  return (
    <footer className="bg-primary-foreground text-primary pt-20 pb-10 border-t border-primary/10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold tracking-wide">
              CITIZEN <span className="text-secondary">TRAVELS</span>
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Discover the pearl of the Indian Ocean. We curate unforgettable journeys through Sri Lanka's most breathtaking landscapes and rich heritage.
            </p>
            <div className="flex gap-4">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="w-10 h-10 rounded-full bg-white border border-primary/10 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-10 h-10 rounded-full bg-white border border-primary/10 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok" className="w-10 h-10 rounded-full bg-white border border-primary/10 flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-xl font-bold mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {["About Us", "Destinations", "Tour Packages", "Travel Blog", "Contact Us"].map((item) => (
                <li key={item}>
                  <a href="#" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-xl font-bold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-muted-foreground">
                <MapPin className="w-5 h-5 text-secondary shrink-0 mt-1" />
                <span data-testid="footer-address">{settings.address}</span>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground">
                <Phone className="w-5 h-5 text-secondary shrink-0" />
                <span data-testid="footer-phones">
                  {settings.phone1}
                  {settings.phone2 ? ` / ${settings.phone2}` : ""}
                </span>
              </li>
              <li className="flex items-center gap-3 text-muted-foreground">
                <Mail className="w-5 h-5 text-secondary shrink-0" />
                <span data-testid="footer-email">{settings.email}</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-serif text-xl font-bold mb-6">Newsletter</h4>
            <p className="text-muted-foreground mb-4">Subscribe to get special offers and travel inspiration.</p>
            <div className="space-y-3">
              <Input placeholder="Your email address" className="bg-white border-primary/20" />
              <Button className="w-full bg-primary text-white hover:bg-primary/90">Subscribe</Button>
            </div>
          </div>
        </div>

        <div className="border-t border-primary/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>© 2026 CITIZEN TRAVELS. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-primary">Privacy Policy</a>
            <a href="#" className="hover:text-primary">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
